/**
 * Pure helpers used by both the state machine and the preview-entity
 * builders.
 *
 * Extracted from `draftStateMachine.ts` so `draftPreviewEntities.ts`
 * can pull these primitives in without re-importing the state-machine
 * module — breaks the depcruise cycle
 *
 *   draftStateMachine.ts ↔ draftPreviewEntities.ts
 *
 * The parent `draftStateMachine.ts` re-exports every symbol so existing
 * import paths keep working. New code should prefer importing directly
 * from this module to keep the dependency graph acyclic.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";
import {
  bulgeArc,
  evaluateNurbs,
  fitPointsToControlPoints,
  nurbsKnotSpanBreakpoints,
  uniformKnotVector,
} from "@dxf-ai-editor/drawing-model";

import {
  resolveReferencePointForDraftTextInput,
  resolveOrthoAnchorForDraftSession,
} from "./drafting/textInputResolver";
// OTRACK-SUBSYSTEM — authoritative point-prompt classification (see
// `draftSessionAcceptsPointPrompt`'s own doc for why the registry, not the
// negative-list helper below, is the correct source here).
import { DRAFT_STEP_REGISTRY } from "./drafting/draftStepRegistry";
import type {
  DraftCommandId,
  DraftPoint,
  DraftSession,
  DraftStep,
} from "./draftTypes";
import { distanceBetween } from "./draftTypes";
import { readPointList } from "./renderHelpers";

export function createPreviewEntity(input: {
  id: string;
  label: string;
  type: EntityPreview["type"];
  spaceId: string;
  summary: string;
  editableProperties: readonly string[];
  properties: Record<string, string>;
}): EntityPreview {
  return {
    id: input.id,
    label: input.label,
    type: input.type,
    spaceId: input.spaceId,
    summary: input.summary,
    editableProperties: [...input.editableProperties],
    properties: input.properties,
  };
}

export function rectangleCorners(
  firstCorner: DraftPoint,
  secondCorner: DraftPoint,
): DraftPoint[] {
  return [
    { x: firstCorner.x, y: firstCorner.y },
    { x: secondCorner.x, y: firstCorner.y },
    { x: secondCorner.x, y: secondCorner.y },
    { x: firstCorner.x, y: secondCorner.y },
  ];
}

export function formatPoint(point: DraftPoint): string {
  return `${formatNumber(point.x)},${formatNumber(point.y)}`;
}

export function formatPointList(points: DraftPoint[]): string {
  return points.map((point) => formatPoint(point)).join(";");
}

export function formatNumber(value: number): string {
  const rounded = Number(value.toFixed(3));
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

export function normalizeAngle(value: number): number {
  return ((value % 360) + 360) % 360;
}

export function toStageAngle(center: DraftPoint, point: DraftPoint): number {
  return normalizeAngle(
    (Math.atan2(point.y - center.y, point.x - center.x) * 180) / Math.PI + 90,
  );
}

function isAngleOnSweep(
  startAngle: number,
  endAngle: number,
  candidateAngle: number,
): boolean {
  const normalizedStart = normalizeAngle(startAngle);
  const normalizedEnd = normalizeAngle(endAngle);
  const normalizedCandidate = normalizeAngle(candidateAngle);
  const endDelta = normalizeAngle(normalizedEnd - normalizedStart);
  const candidateDelta = normalizeAngle(normalizedCandidate - normalizedStart);

  return candidateDelta <= endDelta;
}

export function resolveArcGeometry(points: DraftPoint[]) {
  if (points.length < 3) {
    return null;
  }

  const [start, middle, end] = points;
  const denominator =
    2 *
    (start.x * (middle.y - end.y) +
      middle.x * (end.y - start.y) +
      end.x * (start.y - middle.y));

  if (Math.abs(denominator) < 0.0001) {
    return null;
  }

  const startSquared = start.x ** 2 + start.y ** 2;
  const middleSquared = middle.x ** 2 + middle.y ** 2;
  const endSquared = end.x ** 2 + end.y ** 2;

  const center = {
    x:
      (startSquared * (middle.y - end.y) +
        middleSquared * (end.y - start.y) +
        endSquared * (start.y - middle.y)) /
      denominator,
    y:
      (startSquared * (end.x - middle.x) +
        middleSquared * (start.x - end.x) +
        endSquared * (middle.x - start.x)) /
      denominator,
  };

  const startAngle = toStageAngle(center, start);
  const middleAngle = toStageAngle(center, middle);
  const endAngle = toStageAngle(center, end);

  return {
    center,
    radius: distanceBetween(center, start),
    ...(isAngleOnSweep(startAngle, endAngle, middleAngle)
      ? { startAngle, endAngle }
      : { startAngle: endAngle, endAngle: startAngle }),
  };
}

// ── Helpers moved from `draftingHelpers.ts` to break the depcruise
//    cycle `draftStateMachine.ts` ↔ `draftingHelpers.ts`. The state
//    machine consumes these primitives, while `draftingHelpers.ts`
//    re-exports them so existing import paths keep working. ──────────

export function defaultDraftProperties(
  defaultObjectProperties: Record<string, string>,
): Record<string, string> {
  return {
    Layer: defaultObjectProperties.Layer ?? "0",
    Color: defaultObjectProperties.Color ?? "ByLayer",
    Linetype: defaultObjectProperties.Linetype ?? "ByLayer",
    Lineweight: defaultObjectProperties.Lineweight ?? "ByLayer",
    Transparency: defaultObjectProperties.Transparency ?? "0",
    Annotative: defaultObjectProperties.Annotative ?? "No",
    Thickness: defaultObjectProperties.Thickness ?? "0",
  };
}

/**
 * Phase C.2 — circumscribing-circle helper used by CIRCLE 3P sub-flow.
 *
 * Returns the unique circle passing through three non-collinear points
 * (intersection of the perpendicular bisectors of segments p1-p2 and
 * p2-p3). When the points are collinear (zero-area triangle), returns
 * `null` — callers must handle this by aborting the sub-flow with a
 * "Points are collinear" toast.
 *
 * AutoCAD reference: CIRCLE 3P uses identical math; the resulting
 * circle is the triangle's circumscribed circle.
 */
export function circumscribingCircleFromThree(
  p1: DraftPoint,
  p2: DraftPoint,
  p3: DraftPoint,
): { center: DraftPoint; radius: number } | null {
  const ax = p1.x;
  const ay = p1.y;
  const bx = p2.x;
  const by = p2.y;
  const cx = p3.x;
  const cy = p3.y;
  const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
  if (Math.abs(d) < 1e-10) {
    return null;
  }
  const ax2ay2 = ax * ax + ay * ay;
  const bx2by2 = bx * bx + by * by;
  const cx2cy2 = cx * cx + cy * cy;
  const ux = (ax2ay2 * (by - cy) + bx2by2 * (cy - ay) + cx2cy2 * (ay - by)) / d;
  const uy = (ax2ay2 * (cx - bx) + bx2by2 * (ax - cx) + cx2cy2 * (bx - ax)) / d;
  const radius = Math.hypot(ux - ax, uy - ay);
  if (!Number.isFinite(radius) || radius <= 0) return null;
  return { center: { x: ux, y: uy }, radius };
}

/**
 * POLYGON — generate vertices for a regular N-sided polygon.
 *
 * AutoCAD parity:
 *   - mode = "inscribed" → vertices sit ON the radius circle.
 *   - mode = "circumscribed" → midpoints of the polygon's edges sit on
 *     the radius circle. The polygon's circumradius is therefore
 *     `radius / cos(π/N)`.
 */
export function generatePolygonVertices(
  center: DraftPoint,
  sides: number,
  radius: number,
  mode: "inscribed" | "circumscribed",
): DraftPoint[] {
  const effectiveRadius =
    mode === "circumscribed" ? radius / Math.cos(Math.PI / sides) : radius;
  const startAngle =
    mode === "inscribed" ? Math.PI / 2 : Math.PI / 2 + Math.PI / sides;
  const vertices: DraftPoint[] = [];
  for (let i = 0; i < sides; i += 1) {
    const angle = startAngle + (2 * Math.PI * i) / sides;
    vertices.push({
      x: center.x + effectiveRadius * Math.cos(angle),
      y: center.y + effectiveRadius * Math.sin(angle),
    });
  }
  return vertices;
}

/**
 * Phase C.3 — Start-Center-End ARC end-point projection helper.
 *
 * Given the SCE start, center, and a typed subtended angle (degrees),
 * computes the end point by rotating the (center→start) vector by the
 * subtended angle.
 */
export function arcSceEndPoint(
  start: DraftPoint,
  center: DraftPoint,
  endAngleDeg: number,
): { end: DraftPoint; radius: number; startAngle: number; endAngle: number } {
  const radius = Math.hypot(start.x - center.x, start.y - center.y);
  const startAngle = Math.atan2(start.y - center.y, start.x - center.x);
  const endAngle = startAngle + (endAngleDeg * Math.PI) / 180;
  return {
    end: {
      x: center.x + radius * Math.cos(endAngle),
      y: center.y + radius * Math.sin(endAngle),
    },
    radius,
    startAngle,
    endAngle,
  };
}

export function draftStepAcceptsPointInput(step: DraftStep): boolean {
  return ![
    "text-content",
    "mtext-content",
    "mtext-height",
    "mleader-content",
    "mleaderedit-select",
    "mleaderedit-options",
    "circle-ready",
    "arc-ready",
    "rectangle-ready",
    "block-ready",
    "hatch-ready",
    "dimension-ready",
    "revcloud-ready",
    "wipeout-ready",
    "viewport-ready",
    "move-select",
    "copy-select",
    "mirror-select",
    "rotate-select",
    "scale-select",
    "arrayrect-select",
    "arrayrect-spacing",
    "arraypolar-select",
    "arraypolar-count",
    "arraypolar-fill",
    "arraypath-select",
    "arraypath-method",
    "arraypath-params",
    "offset-distance",
    "trim-select-edges",
    "extend-select-edges",
    "fillet-radius",
    "chamfer-distances",
    "break-select",
    "join-select",
    "explode-select",
    "lengthen-select",
    "lengthen-mode",
    "lengthen-value",
    "block-select-objects",
    "block-dialog",
    "insert-dialog",
    "insert-collect-attribs",
    "bedit-prompt-name",
    "bedit-confirm-create",
    "bclose-confirm",
    "attdef-dialog",
    "ddatte-select",
    "ddatte-dialog",
    "eattedit-select",
    "eattedit-dialog",
    "polyline-width-start",
    "polyline-width-end",
    "arc-sce-end-angle",
    "rectangle-d-length",
    "rectangle-d-width",
    "rotate-ref-new-angle",
    "donut-inner-d",
    "donut-outer-d",
    "polygon-sides-input",
    "polygon-mode-prompt",
    "hatch-mode-prompt",
    // ── HATCHEDIT field prompts (Codex P2-b) ──
    // After the hatch is selected the session holds NO reference points, so
    // the DI overlay's `dimGateOk = !stepAcceptsPoints || points.length >= 1`
    // check would SUPPRESS the dim field for any point-accepting step (the
    // scalar Scale / rotational Angle field would never show, breaking numeric
    // DI for HATCHEDIT). These are text / scalar / rotation prompts — NOT
    // coordinate inputs — exactly like OFFSET-distance / FILLET-radius above,
    // and they match `DRAFT_STEP_REGISTRY[step].acceptsPointInput === false`
    // (draftStepRegistry.ts — the registry is the single-source-of-truth this
    // negative list must mirror; the `draftStepAcceptsPointInput` consistency
    // test enforces parity). `hatchedit-select` stays OUT of this list (it
    // accepts a pick click).
    "hatchedit-pattern",
    "hatchedit-scale",
    "hatchedit-angle",
  ].includes(step);
}

/**
 * OTRACK-SUBSYSTEM — "is `session` CURRENTLY at an active point prompt"
 * (local Codex review round 3 P2 — "Gate OTRACK dwell to point prompts"):
 * a draft command being active is NOT enough on its own — a command can be
 * active at a non-point sub-step too (PLINE's width entry, POLYGON's sides
 * count), and hovering there must not arm a stale acquisition. Reads the
 * AUTHORITATIVE `DRAFT_STEP_REGISTRY` directly (NOT the drift-prone
 * negative list `draftStepAcceptsPointInput` above — same reasoning
 * `pointFilterSession.ts` documents for its own choke point), so a new
 * step wired into the registry is correctly classified without needing a
 * second edit here.
 */
export function draftSessionAcceptsPointPrompt(
  session: DraftSession | null,
): boolean {
  if (!session) return false;
  return DRAFT_STEP_REGISTRY[session.step].acceptsPointInput;
}

/**
 * Steps the registry marks `acceptsPointInput: true` (so the DI overlay /
 * preview treat them as point-ish, and a PICK may advance them) but whose
 * COMMAND-LINE text is an arbitrary free-form NAME, not a coordinate:
 *   - `block-name`     — BLOCK / INSERT "Enter block name"
 *   - `hatch-pattern`  — HATCH "Enter pattern name"
 *
 * At these prompts a bare `FROM` / `M2P` / `MTP` / `.X` is a LEGAL block or
 * pattern name. AutoCAD point filters are a transparent layer over POINT
 * prompts only — never over a name prompt — so the point-filter STARTER must
 * not hijack the keyword here; the name handler must receive the literal text.
 * (Codex PR #504 P3.) The picked-point path is unaffected: a pick never starts
 * a filter, and this predicate only suppresses starting one from typed text.
 */
export function draftStepConsumesFreeTextName(step: DraftStep): boolean {
  return step === "block-name" || step === "hatch-pattern";
}

/**
 * Track 2 Phase 4: the 45-case switch was replaced with table-driven
 * dispatch via `DRAFT_STEP_REGISTRY[step].textInputReference`. The
 * actual mapping logic lives in
 * `apps/web/src/app/scene/drafting/textInputResolver.ts`. This wrapper
 * preserves the historic export name + signature so existing callers
 * (`draftStateMachine.ts`, `draftModifyStateMachine.ts`,
 * `draftingHelpers.ts` re-export) continue to work without churn.
 *
 * **5-site coupling:** 3/5 switches now table-driven (`buildDraftPrompt`
 * Phase 2, `buildDraftPreviewEntity` Phase 3, this one). Remaining:
 * `advanceDraftSessionWithPoint`, `advanceDraftSessionWithText` —
 * Phase 5.
 */
export function referencePointForDraftTextInput(
  session: DraftSession,
): DraftPoint | null {
  return resolveReferencePointForDraftTextInput(session);
}

/**
 * ORTHO (ORTHOMODE / F8) anchor for the given session — the `$LASTPOINT`
 * the 0/90° lock projects the next pick onto. Defaults to
 * {@link referencePointForDraftTextInput}'s reference but honours the
 * registry's explicit `orthoReference` for steps whose preview rubber-bands
 * from a prior point while their typed input is not relative to it
 * (`spline-fit-next`, `arc-sce-center`). See
 * `textInputResolver.resolveOrthoAnchorForDraftSession`. Wrapper preserves
 * the barrel-export shape used by `Scene3DStage` (via `draftingHelpers`).
 */
export function orthoAnchorForDraftSession(
  session: DraftSession,
): DraftPoint | null {
  return resolveOrthoAnchorForDraftSession(session);
}

/**
 * Commands whose `session.points` are consecutive vertices of an ACTUAL
 * drawn straight-segment chain — i.e. the only commands where "the last
 * segment drawn" (AutoCAD POLARMODE relative-basis reference) is even a
 * well-defined concept. LINE and PLINE both let the user place point after
 * point, each new point closing a real straight segment onto the entity
 * being built.
 *
 * Every OTHER multi-point command is excluded for the SAME underlying
 * reason but not always the same surface shape: the distinguishing
 * property is "does this command let the user repeat point-after-point
 * to grow a chain of STRAIGHT segments", not merely "are its points real
 * geometry". CIRCLE's 3-point method collects two points that lie together
 * with a not-yet-picked third on a circle's circumference — the vector
 * between them is not "a segment drawn" in any AutoCAD sense (round 4 P2).
 * ARC/ELLIPSE/RECTANG/POLYGON are the same. SPLINE/REVCLOUD points ARE real
 * defining data, but for a CURVE, not a straight-segment chain — "last
 * segment direction" has no AutoCAD-documented meaning there either.
 * Interactive MLEADER's points (arrow → landing → content) are real segment
 * endpoints too, but there is only ONE segment and no repeatable
 * next-point step to chain further — nothing to be "relative to" beyond
 * what the fewer-than-2-points / degenerate-segment guards below already
 * cover, so it needs no special case (round 5 confirmed no gap here).
 */
const SEGMENT_CHAIN_COMMANDS: ReadonlySet<DraftCommandId> = new Set([
  "LINE",
  "PLINE",
]);

/**
 * POLAR relative-basis (AutoCAD POLARMODE "measure polar angles relative to
 * the last segment drawn") reference angle for the given session, in degrees
 * (unnormalised — may be negative / >360; `buildPolarAngleTable`'s own
 * `norm360` handles wraparound).
 *
 * The reference is the direction of the segment ENDING at the current draft
 * anchor: `session.points[len-2] -> session.points[len-1]` — i.e. the last
 * segment actually drawn by this command, NOT the in-progress rubber band
 * being drawn right now (that would be circular: the segment under the
 * cursor is exactly what polar tracking is trying to help place). Returns
 * `null` when:
 *   - the session's command is not in {@link SEGMENT_CHAIN_COMMANDS} — its
 *     `points` are construction points, not drawn-segment vertices, so
 *     there is no "last segment drawn" to measure (local Codex review
 *     round 4 P2);
 *   - there is no prior segment (fewer than two committed points — the
 *     anchor IS the command's first point, so AutoCAD's relative basis has
 *     nothing to measure against);
 *   - the prior segment is degenerate (zero-length).
 * `polarLockedCursor` treats `null` as "no reference", which
 * `buildPolarAngleTable` maps to a 0° rotation — the SAME table absolute
 * basis would produce, matching AutoCAD's real behaviour that a
 * relative-basis FIRST segment (or a command with no segment concept at
 * all) falls back to the UCS-absolute table.
 *
 * Deliberately NOT filter-aware (unlike
 * {@link orthoAnchorForDraftSessionWithFilter} in `pointFilterSession.ts`):
 * a point-filter's captured sub-point (FROM / M2P / ordinate) is a
 * construction aid, not a drawn entity segment, so "the last segment drawn"
 * always comes from the host session's own committed `points`, filter or not.
 */
export function polarReferenceAngleForDraftSession(
  session: DraftSession | null,
): number | null {
  if (!session) return null;
  if (!SEGMENT_CHAIN_COMMANDS.has(session.command)) return null;
  const { points } = session;
  const len = points.length;
  if (len < 2) return null;
  const from = points[len - 2]!;
  const to = points[len - 1]!;
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (Math.hypot(dx, dy) < 1e-9) return null;
  return (Math.atan2(dy, dx) * 180) / Math.PI;
}

/**
 * Resolve a coarse representative (x,y) coordinate from an
 * `EntityPreview.properties` string-bag for "closest entity" pick
 * heuristics (BREAK/CHAMFER/LENGTHEN/JOIN/EXPLODE/STRETCH select,
 * TRIM/EXTEND/FILLET pick, BPARAM action-select, etc.). Tries, in this
 * priority order, the Center / Start / Insertion / Corner-1 X+Y property
 * PAIR — the first pair where BOTH x and y parse to a finite number wins.
 *
 * #511 P2 (round 2) — the two call sites (`findClosestEntity` here and
 * `findClosestEntityForActionSelect` in `draftStateMachine.blockAuthoring.ts`)
 * used to probe each axis INDEPENDENTLY via `parseFloat(...) || parseFloat(...)
 * || ...`. `parseFloat("0") === 0`, and `0` is falsy in JS, so an entity
 * anchored EXACTLY at x=0 or y=0 (a LINE starting at the origin, extremely
 * common) fell through to the next key group and usually resolved to `NaN` —
 * silently excluding that entity from EVERY "closest entity" pick regardless
 * of click position. Several tests already worked around this by hand-picking
 * non-zero fixture coordinates (`draftingHelpers.test.ts`); the visual-gate
 * BREAK-under-ORTHO scenario did not, and a LINE drawn at world (0,-120) never
 * became a BREAK candidate no matter where the select-click landed. Paired,
 * `Number.isFinite`-gated key groups fix the zero case while preserving the
 * priority order and (realistic) same-group x/y pairing.
 *
 * LWPOLYLINE and Spline previews carry NONE of the four key pairs above —
 * their geometry is a `Vertices` / `Control points` / `Fit points`
 * semicolon-joined point-list string instead (Codex P1, MASTERPLAN N3
 * round 15: TRIM/EXTEND's reducer supported these as TARGETS, but the
 * scene's `findClosestEntity`/`findClosestEntityForActionSelect` pick step
 * couldn't recognise EITHER type at all — clicking one returned no
 * candidate, or silently picked a DIFFERENT nearby entity instead, making
 * the whole feature unreachable through the primary interactive workflow
 * despite being fully implemented in the command-engine/reducer). Falls
 * back to the point in that list NEAREST `nearPoint` (the actual click
 * location, when supplied) — a coarser primitive's fixed Center/Start
 * point is an acceptable approximation for "which entity did I click
 * near", but a multi-vertex curve's FIRST point alone would not be (a
 * click near the far end of a long polyline must still resolve to IT, not
 * an unrelated entity that happens to be nearer that first vertex).
 *
 * `allowPointListFallback` (Codex P2, round 17): the Center/Start/
 * Insertion/Corner-1 branch above is unconditional — it always ran, even
 * pre-round-15 — so every command already tolerated a nearby Circle/Arc/
 * Insert outranking its intended target (a PRE-EXISTING, separately-scoped
 * gap). The point-list branch is what NEWLY makes LWPOLYLINE/Spline
 * selectable at all; several shared callers (FILLET, CHAMFER, BREAK,
 * LENGTHEN) route into reducers that accept ONLY `Line` targets and
 * silently no-op for anything else, so a nearby polyline/spline vertex
 * that now outranks the intended line makes those commands appear to do
 * nothing. Defaults to enabled (every OTHER caller — MOVE/COPY/MIRROR/
 * ROTATE/SCALE/STRETCH/OFFSET/array-select/BACTION-select — accepts any
 * entity type, so the broadened resolution is a strict improvement
 * there); the four Line-only pick steps pass `false` to restore their
 * exact pre-round-15 behaviour.
 */
export function resolveEntityPreviewCoordinate(
  properties: Record<string, string>,
  nearPoint?: { x: number; y: number },
  allowPointListFallback = true,
): { x: number; y: number } | null {
  const keyPairs: ReadonlyArray<readonly [string, string]> = [
    ["Center X", "Center Y"],
    ["Start X", "Start Y"],
    ["Insertion X", "Insertion Y"],
    ["Corner 1 X", "Corner 1 Y"],
  ];
  for (const [xKey, yKey] of keyPairs) {
    const x = parseFloat(properties[xKey] ?? "");
    const y = parseFloat(properties[yKey] ?? "");
    if (Number.isFinite(x) && Number.isFinite(y)) {
      return { x, y };
    }
  }
  if (!allowPointListFallback) return null;
  // Codex P2, round 19: a CLOSED entity's point-list property (`Vertices`
  // for LWPOLYLINE — `entity.closed ? "true" : "false"` — or `Control
  // points`/`Fit points` for a closed Spline — `entity.closed ? "Yes" :
  // "No"`, a DIFFERENT string convention for the same field) does NOT
  // repeat its first point; the closing edge (last point -> first point)
  // is real drawn geometry but absent from the stored list itself. A click
  // directly on that closing edge previously found no local segment to
  // project onto at all, so its distance fell back to the nearest raw
  // endpoint — exactly the round-18 "distance to nearest vertex, not
  // nearest point on the curve" bug, just for the ONE segment `Closed`
  // implies rather than every segment. Case-insensitive so it matches
  // either preview convention.
  const closed = /^(true|yes)$/i.test(properties["Closed"] ?? "");
  const pointListKeys = ["Vertices", "Control points", "Fit points"] as const;
  for (const key of pointListKeys) {
    const pts = readPointList(properties[key]);
    if (pts.length === 0) continue;
    if (!nearPoint) return pts[0]!;
    // Codex P2, round 20: an LWPOLYLINE's `Vertices` preview property has
    // a parallel `Bulges` string (one value per vertex, `vertices[i].bulge`
    // = the bulge of the segment STARTING at vertex i — the DXF/codebase
    // convention throughout this module) — a BULGED segment's drawn
    // geometry is an ARC, not the straight chord `nearestPointOnPolyline`
    // otherwise assumes. Codex's own example: a semicircle (0,0)->(100,0)
    // reports a click on its arc apex near (50,50) as 50 units away (the
    // straight-chord distance), when it is actually ON the entity's own
    // drawn body. Only `Vertices` carries a matching `Bulges` list; Spline
    // previews (`Control points`/`Fit points`) have no bulge concept.
    const bulges =
      key === "Vertices"
        ? (properties["Bulges"] ?? "")
            .split(";")
            .map((s) => parseFloat(s.trim()))
            .map((n) => (Number.isFinite(n) ? n : 0))
        : [];
    // Codex P2, round 21/22: for a Spline, `pts` here is either the raw
    // CONTROL polygon or a straight chord between FIT points — neither is
    // the drawn curve. Codex's own example: a clamped cubic with control
    // points (0,0),(0,100),(100,100),(100,0) passes through (50,75) at its
    // own midpoint, but the control-polygon chord approximation reported
    // the nearest polygon point as 25 units away — enough for an unrelated
    // entity to win `findClosestEntity`'s comparison despite the click
    // landing exactly on the drawn spline (and, symmetrically, an undrawn
    // control-polygon/chord edge could win a click that never touches the
    // actual curve at all — round 22's own example: a fit-point spline
    // with explicit tangents bows visibly away from its own fit-point
    // chords). The preview carries enough (`Degree`/`Knots`/`Weights`
    // alongside `Control points`, or `Degree`/optional tangents alongside
    // `Fit points`) to reconstruct the REAL NURBS and measure against an
    // actual tessellation of it instead.
    if (key === "Control points" || key === "Fit points") {
      const tessellated = tessellateSplinePreview(properties, key, pts);
      if (tessellated) return nearestPointOnPolyline(tessellated, [], nearPoint, closed);
    }
    return nearestPointOnPolyline(pts, bulges, nearPoint, closed);
  }
  return null;
}

/**
 * A coarse tessellation of the REAL NURBS curve a Spline preview's
 * `Control points` OR `Fit points` describes (Codex P2, rounds 21/22 —
 * see `resolveEntityPreviewCoordinate`'s own doc comment). For `Control
 * points`, reconstructed from the SAME `Degree`/`Knots`/`Weights`
 * properties the panel already exposes. For `Fit points` (fit-point
 * mode — `entity.controlPoints` is empty, so `Control points` never
 * reaches this function at all, matching `resolveSplineNurbs`'s own
 * "fit-points mode: never rational" convention), reconstructed via the
 * SAME `fitPointsToControlPoints` the render/TRIM-EXTEND paths already use,
 * honouring the optional `Start tangent`/`End tangent` properties. `null`
 * when a required property is missing or malformed (a partial/legacy
 * preview snapshot) — the caller falls back to the pre-round-21 chord
 * approximation in that case. Sample count scales mildly with point count
 * (never below 32) but is capped at a fixed ceiling (Codex P1, round 22 —
 * `evaluateNurbs` itself scans every control point per call, so an
 * uncapped `samples * controlPointCount` product is genuinely quadratic;
 * a 6,000-control-point imported spline cost ~144 MILLION basis
 * evaluations on every single click otherwise) — coarser than the
 * CAD-core's own 720-sample intersection-grade tessellation elsewhere in
 * this codebase, which is appropriate: this is a "which entity did I
 * click near" UI heuristic, not a geometry computation.
 */
function tessellateSplinePreview(
  properties: Record<string, string>,
  key: "Control points" | "Fit points",
  pts: ReadonlyArray<{ x: number; y: number }>,
): Array<{ x: number; y: number }> | null {
  const degree = parseInt(properties["Degree"] ?? "", 10);
  if (!Number.isFinite(degree) || degree < 1) return null;

  let controlPoints: Array<{ x: number; y: number }>;
  let knots: number[];
  let weights: number[] | undefined;
  if (key === "Control points") {
    controlPoints = readPointList(properties["Control points"]);
    knots = (properties["Knots"] ?? "")
      .split(",")
      .map((s) => parseFloat(s.trim()))
      .filter((n) => Number.isFinite(n));
    // Codex P2 (local review, batch-5 follow-up): a missing/wrong-length
    // knot vector is NOT malformed input to bail out on — `resolveSplineNurbs`
    // (the SAME one-truth normalization `buildSplineBeziers` and the
    // TRIM/EXTEND spline-target module already apply) falls back to
    // `uniformKnotVector` for exactly this case, so the entity still
    // renders a valid curve. Returning `null` here instead made this
    // hit-test fall all the way back to the caller's cruder control-polygon
    // approximation, measuring a click against a shape that can be far
    // from what is actually drawn (e.g. a clamped cubic arch's own
    // midpoint click resolving to the undrawn top polygon edge instead of
    // the rendered curve). Match the SAME normalization instead.
    if (knots.length !== controlPoints.length + degree + 1) {
      knots = uniformKnotVector(controlPoints.length, degree);
    }
    const weightsRaw = (properties["Weights"] ?? "")
      .split(",")
      .map((s) => parseFloat(s.trim()))
      .filter((n) => Number.isFinite(n));
    // Codex P2, round 22: a NON-empty weights array shorter than
    // `controlPoints` is legal input (DXF's weight group code 41 is
    // optional per control point) — `evaluateNurbs`/`resolveSplineNurbs`
    // (Codex P1, round 17 — the SAME class of bug, already fixed there)
    // both default a missing entry to 1 via `weights?.[i] ?? 1`;
    // discarding the whole array here instead of padding made this
    // hit-test measure against a DIFFERENT (fully non-rational) curve
    // than the one actually rendered.
    //
    // Codex P2 (GitHub review, round 2 follow-up): gate on the preview's
    // own authoritative `Rational` property, not merely "is `Weights`
    // non-empty" — matching `resolveSplineNurbs`'s own fix for the
    // identical stale-weights-after-a-Properties-edit failure mode (see
    // that function's doc comment). A retained non-empty `Weights` bag on
    // a `Rational: "No"` preview must NOT construct a weighted NURBS: the
    // nearest-point coordinate would then lie on a curve different from
    // the one actually displayed, letting an unrelated entity win a
    // pick-distance comparison, or making this spline register a hit
    // where the user did not click.
    weights =
      properties["Rational"] === "Yes" && weightsRaw.length > 0
        ? controlPoints.map((_, i) => weightsRaw[i] ?? 1)
        : undefined;
  } else {
    if (pts.length < 2) return null;
    // Codex local review (batch-5 follow-up round 3): `fitPointsToControlPoints`
    // normalizes an explicit tangent by its FULL 3-D (XYZ) magnitude
    // (Codex P2-4, #317) — a 2-D-only `{x,y}` tangent silently implies
    // Z=0, giving a DIFFERENT normalization (and hence a different
    // synthesized helper control point / reconstructed curve shape) than
    // the real 3-D curve the renderer/TRIM path build for a fit-point
    // spline whose explicit tangent has a nonzero Z. "Start/End tangent Z"
    // is optional (older/partial preview snapshots may not carry it) —
    // absent or non-finite falls back to 0, matching `fitPointsToControlPoints`'s
    // own `startTangent.z ?? 0` default for a genuinely 2-D tangent.
    const startTangentX = parseFloat(properties["Start tangent X"] ?? "");
    const startTangentY = parseFloat(properties["Start tangent Y"] ?? "");
    const startTangentZRaw = parseFloat(properties["Start tangent Z"] ?? "");
    const startTangentZ = Number.isFinite(startTangentZRaw) ? startTangentZRaw : 0;
    const startTangent =
      Number.isFinite(startTangentX) && Number.isFinite(startTangentY)
        ? { x: startTangentX, y: startTangentY, z: startTangentZ }
        : undefined;
    const endTangentX = parseFloat(properties["End tangent X"] ?? "");
    const endTangentY = parseFloat(properties["End tangent Y"] ?? "");
    const endTangentZRaw = parseFloat(properties["End tangent Z"] ?? "");
    const endTangentZ = Number.isFinite(endTangentZRaw) ? endTangentZRaw : 0;
    const endTangent =
      Number.isFinite(endTangentX) && Number.isFinite(endTangentY)
        ? { x: endTangentX, y: endTangentY, z: endTangentZ }
        : undefined;
    const fitted = fitPointsToControlPoints([...pts], startTangent, endTangent, degree);
    controlPoints = fitted.controlPoints;
    knots = fitted.knots;
    weights = undefined; // fit-points mode: never rational
  }

  const uMin = knots[degree]!;
  const uMax = knots[knots.length - degree - 1]!;
  if (!(uMax > uMin)) return null;
  const budget = Math.min(256, Math.max(32, controlPoints.length * 4));
  const tessellated: Array<{ x: number; y: number }> = [];
  // Codex P2 (local review, batch-5 follow-up): a GLOBAL uniform-in-u loop
  // (as this used to be) places samples purely by parametric position,
  // oblivious to where the knot vector's own span boundaries actually
  // fall — a valid nonuniform spline whose visible geometry is
  // concentrated in a span narrower than `(uMax-uMin)/budget` gets NO
  // sample inside it, so this hit-test silently replaces that bow with a
  // straight chord to its span's own endpoints even though the renderer
  // (and the command-engine's own tessellator, which already partitions
  // by `nurbsKnotSpanBreakpoints` for exactly this reason) draws the true
  // curve there. Distribute the SAME bounded total budget per DISTINCT
  // knot span instead, so every span gets at least a few samples
  // regardless of how narrow it is parametrically.
  const breakpoints = nurbsKnotSpanBreakpoints(knots, uMin, uMax);
  const spanCount = Math.max(1, breakpoints.length - 1);
  if (spanCount >= budget) {
    // Too many spans to guarantee meaningful per-span coverage within this
    // UI heuristic's own deliberately coarse budget (Codex P1, round 22 —
    // the quadratic `evaluateNurbs` cost guard this function's own doc
    // comment already establishes) — fall back to the previous bounded
    // uniform-across-domain sampling, still strictly capped at `budget`
    // regardless of `spanCount`.
    for (let i = 0; i <= budget; i++) {
      const u = uMin + ((uMax - uMin) * i) / budget;
      const p = evaluateNurbs(controlPoints, knots, weights, degree, u);
      tessellated.push({ x: p.x, y: p.y });
    }
  } else {
    const samplesPerSpan = Math.max(3, Math.floor(budget / spanCount));
    for (let j = 0; j < spanCount; j++) {
      const t0 = breakpoints[j]!;
      const t1 = breakpoints[j + 1]!;
      const startI = j === 0 ? 0 : 1; // skip the duplicate shared boundary sample
      for (let i = startI; i <= samplesPerSpan; i++) {
        const u = t0 + ((t1 - t0) * i) / samplesPerSpan;
        const p = evaluateNurbs(controlPoints, knots, weights, degree, u);
        tessellated.push({ x: p.x, y: p.y });
      }
    }
  }
  return tessellated;
}

/**
 * The point on `pts`' own connecting SEGMENTS nearest `target` — Codex P2,
 * round 18: comparing only the raw vertices/control/fit points (as this
 * function used to) ranks a click squarely on the MIDDLE of a long segment
 * by its distance to the NEAREST ENDPOINT, not to the segment itself — a
 * click at (50,0) on a polyline running (0,0)→(100,0) reported a 50-unit
 * distance, so an unrelated entity with a vertex only 10 units away won
 * `findClosestEntity`'s comparison even though the click landed exactly on
 * the intended entity's own body. `pts` treated as a connected polyline
 * chain is the correct level of fidelity for this string-bag preview layer
 * — a straight-chord approximation between consecutive points is still a
 * materially closer match to the rendered curve than a single nearest
 * point, for all three point-list properties (an LWPOLYLINE's `Vertices`
 * chain IS the drawn geometry for its STRAIGHT portions; a Spline's `Fit
 * points` chain is interpolated THROUGH by the curve; a Spline's `Control
 * points` chain bounds the curve via the convex-hull property). `closed`
 * (Codex P2, round 19) additionally tests the last-point-to-first-point
 * closing segment, which a closed entity's own stored point list never
 * repeats explicitly. `bulges[i]` (Codex P2, round 20, LWPOLYLINE
 * `Vertices` only — empty for Spline's point lists) is the ACTUAL bulge of
 * the segment `pts[i]->pts[i+1]`: when nonzero, the nearest point is
 * computed against the true circular ARC that segment draws, not its
 * straight chord.
 */
function nearestPointOnPolyline(
  pts: ReadonlyArray<{ x: number; y: number }>,
  bulges: ReadonlyArray<number>,
  target: { x: number; y: number },
  closed: boolean,
): { x: number; y: number } {
  if (pts.length === 1) return pts[0]!;
  let best = pts[0]!;
  let bestD = Infinity;
  const segmentCount = closed ? pts.length : pts.length - 1;
  for (let i = 0; i < segmentCount; i++) {
    const a = pts[i]!;
    const b = pts[(i + 1) % pts.length]!;
    const bulge = bulges[i] ?? 0;
    const candidate = nearestPointOnBulgeSegment(a, b, bulge, target);
    const d = (candidate.x - target.x) ** 2 + (candidate.y - target.y) ** 2;
    if (d < bestD) {
      bestD = d;
      best = candidate;
    }
  }
  return best;
}

/** Shortest (<= PI) undirected angular separation between two angles. */
function angularSeparation(a1: number, a2: number): number {
  const twoPi = 2 * Math.PI;
  let d = Math.abs(a1 - a2) % twoPi;
  if (d > Math.PI) d = twoPi - d;
  return d;
}

/** The point on the segment from `a` to `b` — a true circular ARC when
 * `bulge` is nonzero, its straight chord otherwise — nearest `target`. */
function nearestPointOnBulgeSegment(
  a: { x: number; y: number },
  b: { x: number; y: number },
  bulge: number,
  target: { x: number; y: number },
): { x: number; y: number } {
  const arc = bulgeArc(a, b, bulge);
  if (!arc) return nearestPointOnStraightSegment(a, b, target);
  const targetAngle = Math.atan2(target.y - arc.cy, target.x - arc.cx);
  const twoPi = 2 * Math.PI;
  const dir = arc.signedSweep >= 0 ? 1 : -1;
  const sweepMag = Math.abs(arc.signedSweep);
  // How far (always >= 0, wrapped to [0, 2*PI)) `targetAngle` sits from
  // `startAngle`, travelling in the arc's OWN sweep direction.
  const travelled = (((dir * (targetAngle - arc.startAngle)) % twoPi) + twoPi) % twoPi;
  if (travelled <= sweepMag) {
    const angle = arc.startAngle + dir * travelled;
    return { x: arc.cx + arc.radius * Math.cos(angle), y: arc.cy + arc.radius * Math.sin(angle) };
  }
  // `targetAngle` falls OUTSIDE the arc's own angular sweep — the nearest
  // point on a FINITE arc (as opposed to the full circle) is always one of
  // its two endpoints, specifically whichever is angularly CLOSER by the
  // shortest (undirected) separation. Returning the exact `a`/`b` value
  // (rather than re-evaluating via cos/sin at that endpoint's own angle)
  // avoids introducing floating-point noise at an otherwise-exact vertex.
  const endAngle = arc.startAngle + arc.signedSweep;
  return angularSeparation(targetAngle, arc.startAngle) <= angularSeparation(targetAngle, endAngle)
    ? a
    : b;
}

function nearestPointOnStraightSegment(
  a: { x: number; y: number },
  b: { x: number; y: number },
  target: { x: number; y: number },
): { x: number; y: number } {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-12) return a;
  const t = Math.max(
    0,
    Math.min(1, ((target.x - a.x) * dx + (target.y - a.y) * dy) / len2),
  );
  return { x: a.x + t * dx, y: a.y + t * dy };
}
